import type { Lens } from '@hookform/lenses';
import { z } from 'zod/v4';
import OrdersIcon from '@interxfi/design-system/icons/react/icon-orders';
import type {
  IxcOrdersV1OrderControls,
  IxcOrdersV1OrderControlsSettings,
  IxcOrdersV1OrderControlsSettingsUsEquities,
} from '@interxfi/types/generated/api';
import { AccordionFormGroup } from '../../../../../../components/card-group/form/accordion-form-group';
import { AppSwitchInputWithLens } from '../../../../../../components/form/app-switch-input';
import { addDefinedValues } from '../../../../../../util/has-value';

export const programOrderControlsFormSchema = z.object({
  usEquities: z.object({
    canPlaceOpening: z.boolean(),
    canPlaceClosing: z.boolean(),
    canPlaceFractional: z.boolean(),
    canChargeCommission: z.boolean(),
    // notionalHoldBufferPercent: getNumericStringValidator('Notional Hold Buffer Percent', { required: '', gte: 0, lte: 100 }),
  }),
});

export type ProgramOrderControlsFormValues = z.infer<typeof programOrderControlsFormSchema>;

export const programOrderControlsFormDefaultValues: ProgramOrderControlsFormValues = {
  usEquities: {
    canPlaceOpening: false,
    canPlaceClosing: false,
    canPlaceFractional: false,
    canChargeCommission: false,
    // notionalHoldBufferPercent: '',
  },
};

export function getInitialFormValuesForProgramOrdersControls(data: IxcOrdersV1OrderControls | undefined): ProgramOrderControlsFormValues {
  if (!data) {
    return programOrderControlsFormDefaultValues;
  }

  // let notionalHoldBufferPercent = '';
  // if (data.settings?.usEquities?.notionalHoldBufferPercent) {
  //   try {
  //     notionalHoldBufferPercent = new Big(parseNumber(data.settings.usEquities.notionalHoldBufferPercent)).mul(100).toString();
  //   } catch {}
  // }

  return {
    usEquities: {
      canPlaceOpening: Boolean(data.settings?.usEquities?.canPlaceOpening),
      canPlaceClosing: Boolean(data.settings?.usEquities?.canPlaceClosing),
      canPlaceFractional: Boolean(data.settings?.usEquities?.canTradeFractions),
      canChargeCommission: Boolean(data.settings?.usEquities?.canChargeCommission),
      // notionalHoldBufferPercent,
    },
  };
}

function formValuesToProgramUsEquityOrderSettings(values: ProgramOrderControlsFormValues): IxcOrdersV1OrderControlsSettingsUsEquities {
  // let notionalHoldBufferPercent: string | undefined = parseNumber(values.usEquities.notionalHoldBufferPercent);
  // if (!Number.isNaN(Number(notionalHoldBufferPercent))) {
  //   try {
  //     notionalHoldBufferPercent = new Big(notionalHoldBufferPercent).div(100).toString();
  //   } catch {}
  // } else {
  //   notionalHoldBufferPercent = undefined;
  // }

  return {
    canPlaceOpening: values.usEquities.canPlaceOpening,
    canPlaceClosing: values.usEquities.canPlaceClosing,
    canTradeFractions: values.usEquities.canPlaceFractional,
    canChargeCommission: values.usEquities.canChargeCommission,
    // notionalHoldBufferPercent,
  };
}

function formValuesToProgramOrderControlSettings(values: ProgramOrderControlsFormValues): IxcOrdersV1OrderControlsSettings {
  const usEquitySettings = formValuesToProgramUsEquityOrderSettings(values);

  return addDefinedValues<IxcOrdersV1OrderControlsSettings>(
    {},
    {
      usEquities: Object.values(usEquitySettings).filter(Boolean).length > 0 ? usEquitySettings : undefined,
    },
  );
}

export function formValuesToProgramOrdersControls(values: ProgramOrderControlsFormValues): IxcOrdersV1OrderControls {
  const settings = formValuesToProgramOrderControlSettings(values);

  return addDefinedValues<IxcOrdersV1OrderControls>(
    {},
    {
      settings: Object.keys(settings).length > 0 ? settings : undefined,
    },
  );
}

interface ProgramOrderControlsFormProps {
  lens: Lens<ProgramOrderControlsFormValues>;
}

export function ProgramOrderControlsForm({ lens }: ProgramOrderControlsFormProps) {
  return (
    <AccordionFormGroup icon={<OrdersIcon aria-hidden />} title="Orders">
      <AccordionFormGroup.Section
        toggleInputElement={
          <AppSwitchInputWithLens align="space-between" label="Place Opening Orders" lens={lens.focus('usEquities.canPlaceOpening')} />
        }
      />

      <AccordionFormGroup.Section
        toggleInputElement={
          <AppSwitchInputWithLens align="space-between" label="Place Closing Orders" lens={lens.focus('usEquities.canPlaceClosing')} />
        }
      />

      <AccordionFormGroup.Section
        toggleInputElement={
          <AppSwitchInputWithLens align="space-between" label="Trade Fractions" lens={lens.focus('usEquities.canPlaceFractional')} />
        }
      />

      <AccordionFormGroup.Section
        toggleInputElement={
          <AppSwitchInputWithLens align="space-between" label="Charge Commission" lens={lens.focus('usEquities.canChargeCommission')} />
        }
      />

      {/*<AccordionFormGroup.Section>
        <AppTextInputWithLens
          label="Notional Hold Buffer Percent"
          lens={lens.focus('usEquities.notionalHoldBufferPercent')}
          maskOptions={DEFAULT_PERCENTAGE_MASK}
        />
      </AccordionFormGroup.Section>*/}
    </AccordionFormGroup>
  );
}
